/**
 * Lightweight client-side error reporter.
 * Captures uncaught errors and unhandled promise rejections,
 * sends them to POST /api/client-errors where they flow into CloudWatch Logs.
 *
 * All fields are scrubbed and length-capped by reportClientError before they
 * leave the browser so user-controlled strings can't bloat or poison the log.
 */

const MAX_MESSAGE = 2000;
const MAX_STACK = 4000;
const MAX_SHORT = 500;

/** Coerce to string and hard-cap length; drop null/undefined. */
function cap(value: unknown, max: number): string | undefined {
  if (value === undefined || value === null) return undefined;
  return String(value).slice(0, max);
}

/** Origin + pathname only — strips query/hash which can carry tokens or PII. */
function safeLocation(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Single scrubber for every client error report. Only known, bounded fields
 * survive; everything else is dropped before fetch().
 */
export function reportClientError(input: Record<string, unknown>): void {
  const payload = {
    type: cap(input.type, 50),
    message: cap(input.message, MAX_MESSAGE),
    source: cap(input.source, MAX_SHORT),
    stack: cap(input.stack, MAX_STACK),
    componentStack: cap(input.componentStack, MAX_STACK),
    line: typeof input.line === 'number' ? input.line : undefined,
    col: typeof input.col === 'number' ? input.col : undefined,
    url: cap(input.url ?? safeLocation(), MAX_SHORT),
    userAgent: cap(input.userAgent ?? navigator.userAgent, MAX_SHORT),
  };

  fetch('/api/client-errors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

window.addEventListener('error', (event) => {
  reportClientError({
    type: 'error',
    message: event.message,
    source: event.filename,
    line: event.lineno,
    col: event.colno,
    stack: event.error?.stack,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { stack?: string } | undefined;
  reportClientError({
    type: 'unhandledrejection',
    message: String(event.reason),
    stack: reason?.stack,
  });
});
